import { Box, CssBaseline, LinearProgress, styled } from "@mui/material";
import { Suspense, useEffect } from "react";
import { Outlet, useLocation } from "react-router";
import { ThemeProvider } from "../../../styles/theme/theme-provider";
import { SettingsConsumer } from "../../../utils/contexts/settings-context";
import { SidebarProvider } from "../../../utils/contexts/sidebar-context";
import { useSidebar2026, useSidebarWidth } from "../hooks/use-sidebar-2026";
import { AppToaster } from "./app-toaster";
import { CeAdminSidebar } from "./ce-admin-sidebar";
import { Sidebar } from "./sidebar/sidebar";
import { Sidebar2026 } from "./sidebar/sidebar-2026";
import { SidebarUiProvider, useSidebarUi } from "./sidebar/sidebar-ui-context";
import { sidebarWidth } from "./sidebar/sidebar-widths";

import type { DisplayTheme } from "../../../utils/contexts/settings-context";
import type { FC } from "react";

const LayoutRoot = styled("div", {
  shouldForwardProp: (prop) => prop !== "sidebarWidth",
})<{ sidebarWidth: number }>(({ theme, sidebarWidth: width }) => ({
  display: "flex",
  flex: "1 1 auto",
  maxWidth: "100%",
  [theme.breakpoints.up("lg")]: {
    paddingLeft: width,
  },
  paddingTop: 0,
}));

export const LightLayout: FC = () => <Layout themeOverride="light" />;

export const Layout: FC<{ themeOverride?: DisplayTheme }> = (props) => (
  <SidebarUiProvider>
    <LayoutInner {...props} />
  </SidebarUiProvider>
);

const LayoutInner: FC<{ themeOverride?: DisplayTheme }> = ({ themeOverride }) => {
  useEffect(() => {
    document.body.style.overflow = "auto";
  }, []);

  const { pathname } = useLocation();
  const { closeSidebar } = useSidebarUi();
  useEffect(() => {
    closeSidebar();
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- pathname is the trigger: close the sidebar on every navigation
  }, [closeSidebar, pathname]);
  const sidebar2026 = useSidebar2026();
  const width = useSidebarWidth();

  return (
    <SettingsConsumer>
      {({ settings }) => (
        <ThemeProvider mode={themeOverride ?? settings.theme}>
          <SidebarProvider>
            <CssBaseline />
            <AppToaster />
            <LayoutRoot sidebarWidth={width}>
              <Box
                sx={{
                  display: "flex",
                  flex: "1 1 auto",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Suspense fallback={<LinearProgress />}>
                  <Outlet />
                </Suspense>
              </Box>
            </LayoutRoot>
            {sidebar2026 ? <Sidebar2026 /> : <Sidebar />}
          </SidebarProvider>
        </ThemeProvider>
      )}
    </SettingsConsumer>
  );
};

export const CeAdminLayout: FC = () => (
  <SidebarUiProvider>
    <CeAdminLayoutInner />
  </SidebarUiProvider>
);

const CeAdminLayoutInner: FC = () => {
  useEffect(() => {
    document.body.style.overflow = "auto";
  }, []);

  const { pathname } = useLocation();
  const { closeSidebar } = useSidebarUi();
  useEffect(() => {
    closeSidebar();
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- pathname is the trigger: close the sidebar on every navigation
  }, [closeSidebar, pathname]);

  return (
    <ThemeProvider mode="ce-admin">
      <AppToaster />
      {/* CE Admin keeps the original sidebar, flag or not. */}
      <LayoutRoot sidebarWidth={sidebarWidth}>
        <Box
          sx={{
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Outlet />
        </Box>
      </LayoutRoot>

      <CeAdminSidebar />
    </ThemeProvider>
  );
};
